/**
 * Laboratory catalogue. Each lab belongs to a department (departmentCode
 * matches a DEPARTMENTS key) and has a detail page at /labs/:slug.
 * Mirrors the `labs` collection in content.json.
 *
 * CSE's labs are hand-authored as rich exemplars; every other department's
 * labs are generated deterministically from a department's lab names (tool
 * hints in parentheses become software licenses) so each lab still gets a
 * detail page.
 *
 * STATUS: this catalogue is invented, and since the department pages moved onto
 * the institute's published laboratory tables nothing links to it any more. It
 * is kept compiling, on its own copy of the old department metadata, so the
 * department migration does not silently delete a route. Migrate it onto
 * `Department.labs` or remove it.
 */

/**
 * Department metadata this catalogue used to read from departments.ts.
 *
 * departments.ts now carries the institute's real published data, where the
 * laboratory list is a specification table rather than a list of names. This
 * catalogue was generated from the old invented names, so it keeps its own copy
 * rather than block the migration. Nothing links here today — see the module
 * header.
 */
const LEGACY_DEPTS: Record<string, { name: string; short: string; hod?: string; labs: string[] }> = {
  CSE: {
    name: "Computer Science & Engineering",
    short: "CSE",
    hod: "Dr. Nirmala C R",
    labs: [
      "Algorithms & Data Structures Lab",
      "Operating Systems & Systems Programming Lab",
      "Database Management Systems Lab",
      "Computer Networks Lab",
      "Machine Learning & AI Lab",
      "Software Engineering & DevOps Lab",
      "Cyber Security & Cryptography Lab",
      "Cloud Computing Lab (AWS Academy)",
    ],
  },
  AIML: {
    name: "Artificial Intelligence & Machine Learning",
    short: "AI & ML",
    hod: "Dr. Anitha H M",
    labs: [
      "Deep Learning & GPU Lab",
      "Computer Vision Lab",
      "Natural Language Processing Lab",
      "Generative AI & LLM Lab (IBM Watson Studio)",
      "Robotics & Reinforcement Learning Lab",
      "Data Engineering Lab",
    ],
  },
  ISE: {
    name: "Information Science & Engineering",
    short: "ISE",
    hod: "Dr. Asha T",
    labs: [
      "Web Technologies Lab",
      "Mobile Application Development Lab",
      "Database & Big Data Lab",
      "Cloud Computing Lab",
      "Information Security Lab",
      "Software Project Lab",
    ],
  },
  ECE: {
    name: "Electronics & Communication Engineering",
    short: "ECE",
    hod: "Dr. Mahesh P K",
    labs: [
      "VLSI Design Lab (Cadence, Synopsys)",
      "Embedded Systems & IoT Lab",
      "Digital Signal Processing Lab",
      "Communication Systems Lab",
      "Microwave & Antenna Lab",
      "Analog & Digital Electronics Lab",
      "PCB Design & Fabrication Lab",
    ],
  },
  EEE: {
    name: "Electrical & Electronics Engineering",
    short: "EEE",
    hod: "Dr. Veerabhadrappa Algur",
    labs: [
      "Electrical Machines Lab",
      "Power Systems Lab",
      "Power Electronics Lab",
      "Control Systems Lab",
      "Renewable Energy Lab (Solar PV, Wind)",
      "High Voltage Engineering Lab",
      "Microcontroller & Drives Lab",
    ],
  },
  MECH: {
    name: "Mechanical Engineering",
    short: "MECH",
    hod: "Dr. Manjunatha L H",
    labs: [
      "Strength of Materials Lab",
      "Thermal & Fluid Mechanics Lab",
      "CAD / CAM Center",
      "Manufacturing & Workshop",
      "CNC & Additive Manufacturing Lab",
      "Robotics & Mechatronics Lab",
      "Automotive Lab",
      "Metallurgy & Materials Lab",
    ],
  },
  CIVIL: {
    name: "Civil Engineering",
    short: "CIVIL",
    hod: "Dr. Kiran G S",
    labs: [
      "Structural Engineering Lab (UTM, fatigue rig)",
      "Concrete & Materials Testing Lab",
      "Geotechnical Engineering Lab",
      "Environmental Engineering Lab",
      "Transportation Engineering Lab",
      "Surveying & GIS Lab",
      "Computer Aided Design Studio (STAAD, ETABS, Revit)",
    ],
  },
  IPE: {
    name: "Industrial & Production Engineering",
    short: "IPE",
    hod: "Dr. Ravichandra M S",
    labs: [
      "Production Engineering Lab",
      "Industrial Engineering & OR Lab",
      "Quality Engineering & Metrology Lab",
      "Operations Management Simulation Lab",
      "Ergonomics Lab",
    ],
  },
  TC: {
    name: "Telecommunication Engineering",
    short: "TC",
    hod: "Dr. Suresh K B",
    labs: [
      "Optical Communication Lab",
      "Wireless Communication Lab",
      "Satellite Communication Lab",
      "Microwave Engineering Lab",
      "Software Defined Radio Lab",
      "Network Programming Lab",
    ],
  },
  MBA: {
    name: "Master of Business Administration",
    short: "MBA",
    labs: [
      "Analytics & Business Intelligence Lab",
      "Finance & Trading Lab",
      "Behavioural Research Lab",
    ],
  },
  MCA: {
    name: "Master of Computer Applications",
    short: "MCA",
    labs: [
      "Full-Stack Development Lab",
      "Database Systems Lab",
      "Cloud & DevOps Lab",
      "Mobile Application Lab",
    ],
  },
};

export interface LabSpec {
  /** Equipment or software name. */
  name: string;
  /** One-line description — quantity, version, what it's used for. */
  detail: string;
}

export interface Lab {
  slug: string;
  /** Owning department code (matches a DEPARTMENTS key, e.g. "CSE"). */
  departmentCode: string;
  /** Full name — matches the department's `labs[]` string exactly. */
  name: string;
  /** Short label / chip — e.g. "ML & AI". */
  short: string;
  /** Single-picture-view label (Placeholder until real imagery lands). */
  image: string;
  /** Gallery labels — at least three. */
  gallery: string[];
  /** One-line summary shown on the card and detail hero. */
  summary: string;
  /** Long-form intro paragraphs. */
  intro: string[];
  established?: number;
  location: string;
  incharge?: string;
  /** Seat capacity, e.g. "60 students · 30 workstations". */
  capacity?: string;
  /** Major equipment / hardware. */
  equipment: LabSpec[];
  /** Software & licenses. */
  software: LabSpec[];
}

const EXPLICIT_LABS: Lab[] = [
  {
    slug: "algorithms-data-structures",
    departmentCode: "CSE",
    name: "Algorithms & Data Structures Lab",
    short: "Algo & DS",
    image: "CSE · ALGORITHMS LAB",
    gallery: ["ALGORITHMS LAB · WORKSTATIONS", "STUDENTS AT WORK", "COMPETITIVE PROGRAMMING WALL"],
    summary: "Where first- and second-year students build the fundamentals — data structures, complexity, and disciplined problem solving.",
    intro: [
      "The Algorithms & Data Structures Lab is the foundation lab of the department. Students implement core data structures and algorithms from scratch and benchmark them against standard library implementations.",
      "The lab doubles as the home of the department's competitive-programming club, which runs weekly practice contests on the in-house judge.",
    ],
    established: 1998,
    location: "CSE Block · Ground Floor · Room G-04",
    incharge: "Prof. Anitha K",
    capacity: "60 students · 60 workstations",
    equipment: [
      { name: "60× Student workstations", detail: "Intel i5, 16 GB RAM, dual-boot Ubuntu / Windows" },
      { name: "Instructor console + projector", detail: "Live code-casting to all student screens" },
      { name: "Local judge server", detail: "Self-hosted DOMjudge for practice contests" },
    ],
    software: [
      { name: "GCC / Clang toolchain", detail: "Open-source · C / C++ standard" },
      { name: "JetBrains CLion (educational)", detail: "Classroom licence · 60 seats" },
      { name: "Visual Studio Code", detail: "Open-source · with C++ / Python extensions" },
    ],
  },
  {
    slug: "operating-systems",
    departmentCode: "CSE",
    name: "Operating Systems & Systems Programming Lab",
    short: "OS & Sys",
    image: "CSE · OPERATING SYSTEMS LAB",
    gallery: ["OS LAB · TERMINALS", "KERNEL DEBUGGING SESSION", "RACK · LAB SERVERS"],
    summary: "Hands-on systems programming — processes, threads, scheduling, file systems and kernel internals.",
    intro: [
      "Students work directly with Linux internals: writing system calls, implementing schedulers, and instrumenting the kernel. The lab is configured for safe low-level experimentation with snapshot-and-restore virtual machines.",
    ],
    established: 2001,
    location: "CSE Block · First Floor · Room 112",
    incharge: "Prof. Ramesh B",
    capacity: "60 students · 30 workstations (shared)",
    equipment: [
      { name: "30× Dual-monitor workstations", detail: "Intel i7, 32 GB RAM — for VM-heavy workloads" },
      { name: "Virtualisation host", detail: "Proxmox cluster for per-student kernel sandboxes" },
    ],
    software: [
      { name: "Linux (Ubuntu / Arch)", detail: "Open-source · with full kernel source trees" },
      { name: "VMware Workstation", detail: "Academic licence · 30 seats" },
      { name: "QEMU / KVM + GDB", detail: "Open-source · kernel debugging" },
    ],
  },
  {
    slug: "dbms",
    departmentCode: "CSE",
    name: "Database Management Systems Lab",
    short: "DBMS",
    image: "CSE · DBMS LAB",
    gallery: ["DBMS LAB · WORKSTATIONS", "ER-MODELLING WHITEBOARD", "QUERY-TUNING SESSION"],
    summary: "Relational and NoSQL database design, query optimisation, transactions and data modelling.",
    intro: [
      "From entity-relationship modelling to query tuning and transaction isolation, the DBMS lab covers the full lifecycle of data systems on both relational and document databases.",
    ],
    established: 2000,
    location: "CSE Block · First Floor · Room 118",
    incharge: "Dr. Nirmala C R",
    capacity: "60 students · 60 workstations",
    equipment: [
      { name: "60× Student workstations", detail: "Intel i5, 16 GB RAM" },
      { name: "Database server", detail: "Shared instance for multi-user transaction labs" },
    ],
    software: [
      { name: "Oracle Database (Academic)", detail: "Oracle Academy programme licence" },
      { name: "PostgreSQL / MySQL", detail: "Open-source" },
      { name: "MongoDB", detail: "Community edition · NoSQL module" },
    ],
  },
  {
    slug: "computer-networks",
    departmentCode: "CSE",
    name: "Computer Networks Lab",
    short: "Networks",
    image: "CSE · NETWORKS LAB",
    gallery: ["NETWORKS LAB · PATCH PANEL", "ROUTER / SWITCH RACK", "PACKET-CAPTURE SESSION"],
    summary: "Protocol implementation, socket programming, and hands-on routing & switching on real hardware.",
    intro: [
      "Students configure real routers and switches, implement protocols over sockets, and analyse live traffic. The lab maintains an isolated network segment for safe experimentation.",
    ],
    established: 2003,
    location: "CSE Block · Second Floor · Room 204",
    incharge: "Prof. Ramesh B",
    capacity: "60 students · 30 benches",
    equipment: [
      { name: "Cisco router & switch pods", detail: "6 pods for routing & switching labs" },
      { name: "Managed patch panel + isolated segment", detail: "Sandboxed network for experiments" },
    ],
    software: [
      { name: "Cisco Packet Tracer", detail: "Cisco Networking Academy licence" },
      { name: "Wireshark", detail: "Open-source · packet analysis" },
      { name: "GNS3", detail: "Open-source · network emulation" },
    ],
  },
  {
    slug: "machine-learning-ai",
    departmentCode: "CSE",
    name: "Machine Learning & AI Lab",
    short: "ML & AI",
    image: "CSE · ML / AI LAB",
    gallery: ["ML LAB · GPU WORKSTATIONS", "GPU CLUSTER RACK", "MODEL-TRAINING DASHBOARD"],
    summary: "GPU-accelerated machine learning, deep learning and computer-vision research and coursework.",
    intro: [
      "The ML & AI Lab is the department's flagship lab, with a shared GPU cluster supporting both coursework and the Centre for AI & Data Science. Students train and deploy models on real datasets.",
      "It is the venue for the department's applied-AI capstone projects and industry-sponsored work.",
    ],
    established: 2019,
    location: "CSE Block · Second Floor · Room 210",
    incharge: "Dr. Nirmala C R",
    capacity: "40 students · 20 GPU workstations",
    equipment: [
      { name: "8× NVIDIA A4000 GPU cluster", detail: "Shared training infrastructure" },
      { name: "20× GPU workstations", detail: "NVIDIA RTX 3060, 32 GB RAM" },
      { name: "Edge-AI kits", detail: "Jetson Nano / Coral boards for on-device inference" },
    ],
    software: [
      { name: "PyTorch / TensorFlow", detail: "Open-source · deep-learning frameworks" },
      { name: "NVIDIA CUDA toolkit", detail: "GPU compute" },
      { name: "IBM Watson Studio (SkillsBuild)", detail: "Industry partner programme" },
    ],
  },
  {
    slug: "software-engineering-devops",
    departmentCode: "CSE",
    name: "Software Engineering & DevOps Lab",
    short: "SE & DevOps",
    image: "CSE · SOFTWARE ENGINEERING LAB",
    gallery: ["SE LAB · TEAM PODS", "CI/CD DASHBOARD WALL", "AGILE STANDUP BOARD"],
    summary: "Full-cycle software engineering — version control, testing, CI/CD pipelines and team projects.",
    intro: [
      "Organised around team pods rather than rows, this lab runs project-based courses where students ship real software through Git workflows, automated testing and continuous deployment pipelines.",
    ],
    established: 2016,
    location: "CSE Block · Third Floor · Room 304",
    incharge: "Prof. Anitha K",
    capacity: "48 students · 8 team pods",
    equipment: [
      { name: "8× Team pods", detail: "Shared displays + 6 workstations each" },
      { name: "CI/CD build server", detail: "Self-hosted runners for student pipelines" },
    ],
    software: [
      { name: "GitHub Education", detail: "Org licence · classrooms & Actions" },
      { name: "Docker / Kubernetes", detail: "Open-source · containerisation" },
      { name: "JetBrains IntelliJ IDEA (educational)", detail: "Classroom licence" },
    ],
  },
  {
    slug: "cyber-security",
    departmentCode: "CSE",
    name: "Cyber Security & Cryptography Lab",
    short: "CyberSec",
    image: "CSE · CYBER SECURITY LAB",
    gallery: ["CYBERSEC LAB · ISOLATED NETWORK", "CAPTURE-THE-FLAG ARENA", "FORENSICS WORKSTATION"],
    summary: "Applied security — cryptography, penetration testing, digital forensics and capture-the-flag.",
    intro: [
      "The Cyber Security Lab runs on a fully air-gapped network so students can practise offensive and defensive techniques safely. It hosts the department's annual capture-the-flag competition.",
    ],
    established: 2018,
    location: "CSE Block · Third Floor · Room 310",
    incharge: "Dr. Nirmala C R",
    capacity: "30 students · 30 workstations",
    equipment: [
      { name: "Air-gapped lab network", detail: "Isolated segment for offensive-security exercises" },
      { name: "30× Hardened workstations", detail: "Snapshot-and-restore between sessions" },
      { name: "Hardware security kits", detail: "HSMs and smart-card readers for crypto labs" },
    ],
    software: [
      { name: "Kali Linux", detail: "Open-source · penetration-testing distribution" },
      { name: "Wireshark / Metasploit", detail: "Open-source · analysis & exploitation" },
      { name: "Autopsy", detail: "Open-source · digital forensics" },
    ],
  },
  {
    slug: "cloud-computing",
    departmentCode: "CSE",
    name: "Cloud Computing Lab (AWS Academy)",
    short: "Cloud",
    image: "CSE · CLOUD COMPUTING LAB",
    gallery: ["CLOUD LAB · WORKSTATIONS", "AWS ACADEMY SESSION", "ARCHITECTURE WHITEBOARD"],
    summary: "Cloud-native architecture, virtualisation and large-scale systems, delivered as an AWS Academy member lab.",
    intro: [
      "As an AWS Academy member institution, the department delivers cloud coursework with official curriculum and credits. Students design, deploy and scale cloud-native applications.",
    ],
    established: 2021,
    location: "CSE Block · Third Floor · Room 316",
    incharge: "Prof. Ramesh B",
    capacity: "60 students · 60 workstations",
    equipment: [
      { name: "60× Thin-client workstations", detail: "Browser-based access to cloud consoles" },
      { name: "On-prem virtualisation host", detail: "For private-cloud / OpenStack exercises" },
    ],
    software: [
      { name: "AWS Academy", detail: "Member-institution programme · curriculum + credits" },
      { name: "Terraform", detail: "Open-source · infrastructure as code" },
      { name: "OpenStack", detail: "Open-source · private-cloud labs" },
    ],
  },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function shortLabel(baseName: string): string {
  return baseName.replace(/\s*(Laboratory|Lab|Centre|Center|Studio)\s*$/i, "").trim() || baseName;
}

/** Build a reasonable Lab from a department + lab name (used for non-CSE labs). */
function generateLab(code: string, name: string): Lab {
  const dept = LEGACY_DEPTS[code];
  const m = name.match(/^([^(]+?)\s*\(([^)]*)\)\s*$/);
  const baseName = m ? m[1].trim() : name.trim();
  const tools = m ? m[2].split(",").map((t) => t.trim()).filter(Boolean) : [];
  const short = shortLabel(baseName);
  const SHORT = short.toUpperCase();
  const software: LabSpec[] = tools.length
    ? tools.map((t) => ({ name: t, detail: "Licensed software used in this lab" }))
    : [
        { name: "Departmental software suite", detail: "Open-source and licensed tools per the curriculum" },
        { name: "Simulation / analysis tools", detail: `Standard ${short.toLowerCase()} software` },
      ];
  return {
    slug: `${code.toLowerCase()}-${slugify(baseName)}`,
    departmentCode: code,
    name,
    short,
    image: `${code} · ${baseName.toUpperCase()}`,
    gallery: [`${SHORT} · OVERVIEW`, `${SHORT} · EQUIPMENT`, `${SHORT} · IN SESSION`],
    summary: `The ${baseName} supports ${dept?.name ?? code} coursework and projects with dedicated equipment and software.`,
    intro: [
      `The ${name} is part of the ${dept?.name ?? code} department's laboratory complex. Students use it for practical coursework, mini-projects and skill development aligned with the curriculum.`,
      tools.length
        ? `Industry-standard tools used here include ${tools.join(", ")}.`
        : "The lab is equipped with the standard instrumentation and software the curriculum requires.",
    ],
    location: `${dept?.short ?? code} Block · Laboratory complex`,
    incharge: dept?.hod,
    capacity: "Up to 60 students per batch",
    equipment: [
      { name: "Student workstations / benches", detail: `Configured for ${dept?.short ?? code} practical sessions` },
      { name: "Instrumentation & measurement kit", detail: `Standard ${short.toLowerCase()} apparatus` },
    ],
    software,
  };
}

/** Hand-authored CSE labs + generated labs for every other department. */
export const LABS: Lab[] = (() => {
  const explicitKeys = new Set(EXPLICIT_LABS.map((l) => `${l.departmentCode}|${l.name}`));
  const generated: Lab[] = [];
  for (const code of Object.keys(LEGACY_DEPTS)) {
    for (const name of LEGACY_DEPTS[code].labs) {
      if (explicitKeys.has(`${code}|${name}`)) continue;
      generated.push(generateLab(code, name));
    }
  }
  return [...EXPLICIT_LABS, ...generated];
})();

export function getLab(slug: string): Lab | undefined {
  return LABS.find((l) => l.slug === slug);
}

/** Labs belonging to a department. Used by the department detail page. */
export function getLabsByDept(code: string): Lab[] {
  return LABS.filter((l) => l.departmentCode === code);
}

/** Look up a lab by department + exact name (department `labs[]` entries are names). */
export function getLabByName(code: string, name: string): Lab | undefined {
  return LABS.find((l) => l.departmentCode === code && l.name === name);
}
